import { generateShareableURL } from "../utils/urlUtils";

/**
 * Service for handling code sharing operations
 */

export interface ShareResult {
	success: boolean;
	url?: string;
	error?: string;
}

/**
 * Copies text to clipboard with fallback for older browsers
 */
const copyToClipboard = async (text: string): Promise<boolean> => {
	try {
		// Try modern clipboard API first
		await navigator.clipboard.writeText(text);
		return true;
	} catch (error) {
		console.warn("Modern clipboard API failed, trying fallback:", error);

		// Fallback for older browsers
		try {
			const textArea = document.createElement("textarea");
			textArea.value = text;
			textArea.style.position = "fixed";
			textArea.style.opacity = "0";
			document.body.appendChild(textArea);
			textArea.select();
			const success = document.execCommand("copy");
			document.body.removeChild(textArea);
			return success;
		} catch (fallbackError) {
			console.error("Clipboard fallback also failed:", fallbackError);
			return false;
		}
	}
};

/**
 * Shares code by generating a URL and copying it to clipboard
 */
export const shareCode = async (code: string): Promise<ShareResult> => {
	try {
		const shareUrl = generateShareableURL(code);

		const clipboardSuccess = await copyToClipboard(shareUrl);

		if (clipboardSuccess) {
			return {
				success: true,
				url: shareUrl,
			};
		} else {
			return {
				success: false,
				url: shareUrl,
				error: "Failed to copy to clipboard",
			};
		}
	} catch (error) {
		const errorMessage =
			error instanceof Error ? error.message : "Unknown error";
		return {
			success: false,
			error: `Failed to share code: ${errorMessage}`,
		};
	}
};

/**
 * Validates if code can be shared (e.g., URL length limits)
 */
export const validateCodeForSharing = (
	code: string,
): { valid: boolean; warning?: string } => {
	const shareUrl = generateShareableURL(code);
	const MAX_URL_LENGTH = 8000; // Increased limit since compression is more efficient
	const WARN_URL_LENGTH = 4000; // Warning threshold

	if (shareUrl.length > MAX_URL_LENGTH) {
		return {
			valid: false,
			warning: `Code is too large for URL sharing (${shareUrl.length} characters). Consider reducing code size or breaking into smaller examples.`,
		};
	}

	if (shareUrl.length > WARN_URL_LENGTH) {
		return {
			valid: true,
			warning: `Large code size (${shareUrl.length} characters). URL may be long but should work fine.`,
		};
	}

	return { valid: true };
};

/**
 * Gets a shareable URL for code without copying to clipboard
 */
export const getShareableURL = (code: string): string => {
	return generateShareableURL(code);
};
